/**
 * @Relay no-runtime
 */
/**
 * @graphql cc
 */
import graphql from 'graphql-tag';
import { useMemo } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from 'react-apollo';

import { usePageContentId } from '@confluence/page-context';
import { ContentStatus } from '@confluence/page-utils/entry-points/enums';
import { useContentStatus } from '@confluence/page-utils/entry-points/useContentStatus';
import { fg } from '@confluence/feature-gating';

import { normalizeContentCreatorData } from '../normalizers/normalizeContentCreatorData';

import type {
	useContentCreatorDetailsPanelContentCreatedByQuery as useContentCreatorDetailsPanelContentCreatedByQuery$data,
	useContentCreatorDetailsPanelContentCreatedByQueryVariables as useContentCreatorDetailsPanelContentCreatedByQuery$variables,
} from './__types__/useContentCreatorDetailsPanelContentCreatedByQuery';
import { useContentOwner } from './useContentOwner';

const useContentCreatorDetailsPanelContentCreatedByQuery = graphql`
	query useContentCreatorDetailsPanelContentCreatedByQuery(
		$contentId: ID!
		$status: String!
		$includeGeneratedBy: Boolean = false
	) {
		content(id: $contentId, status: [$status]) {
			nodes {
				id
				history {
					createdBy {
						type
						displayName
						... on KnownUser {
							accountId
						}
						profilePicture {
							path
						}
					}
				}
				contentProperties @include(if: $includeGeneratedBy) {
					latest {
						generatedBy
					}
				}
			}
		}
	}
`;
export const useContentCreator = () => {
	const [contentId] = usePageContentId();

	const {
		contentStatus,
		loading: isContentStatusLoading,
		error: contentStatusError,
		refetch: refetchContentStatus,
	} = useContentStatus();

	const isContentStatusDraft = contentStatus === ContentStatus.DRAFT;

	// Feature flag to control whether to fetch generatedBy field
	const includeGeneratedBy = fg('confluence_frontend_confluence_creator_onboarding');

	const {
		data: createdByData,
		loading: isCreatedByLoading,
		error: createdByError,
		refetch: refetchCreatedBy,
	} = useQuery<
		useContentCreatorDetailsPanelContentCreatedByQuery$data,
		useContentCreatorDetailsPanelContentCreatedByQuery$variables
	>(useContentCreatorDetailsPanelContentCreatedByQuery, {
		variables: {
			contentId: contentId ?? '',
			status: contentStatus ?? '',
			includeGeneratedBy,
		},
		skip:
			isContentStatusLoading ||
			!!contentStatusError ||
			!contentStatus ||
			!contentId ||
			isContentStatusDraft,
	});
	const normalizedCreatedByData = useMemo(
		() => normalizeContentCreatorData(createdByData),
		[createdByData],
	);

	const {
		data: contentOwnerData,
		loading: isContentOwnerLoading,
		error: contentOwnerError,
		refetch: refetchContentOwner,
	} = useContentOwner({
		skip: !isContentStatusDraft,
	});

	const refetchUserData = isContentStatusDraft ? refetchContentOwner : refetchCreatedBy;

	return {
		data: isContentStatusDraft ? contentOwnerData : normalizedCreatedByData,
		loading: isContentStatusLoading || isCreatedByLoading || isContentOwnerLoading,
		error: contentStatusError || createdByError || contentOwnerError,
		refetch: contentStatusError ? refetchContentStatus : refetchUserData,
	};
};
